import { useQuery } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  FilterDropdownOption,
  UsersFilterDropdowns,
} from "../../types/users";
import { getKycStatusFilterOptions } from "../../utils/kycStatus";

type StoredToken = { AccessToken?: string };

export const GET_USERS_FILTER_DROPDOWNS_QUERY_KEY =
  "GET_USERS_FILTER_DROPDOWNS";

const EMPTY_RESPONSE: UsersFilterDropdowns = {
  statuses: [],
  kyc_statuses: [],
  ftd: [],
  states: [],
};

// Tolerant of `{value, label}` objects, plain strings, and missing labels.
function normalizeOption(raw: unknown): FilterDropdownOption | null {
  if (!raw) return null;
  if (typeof raw === "string") {
    const v = raw.trim();
    return v ? { value: v, label: v } : null;
  }
  if (typeof raw === "object") {
    const r = raw as { value?: unknown; label?: unknown };
    const value = r.value != null ? String(r.value).trim() : "";
    if (!value) return null;
    const label = r.label != null ? String(r.label).trim() : value;
    return { value, label: label || value };
  }
  return null;
}

function normalizeList(raw: unknown): FilterDropdownOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(normalizeOption)
    .filter((o): o is FilterDropdownOption => Boolean(o));
}

async function fetchUsersFilterDropdowns(): Promise<UsersFilterDropdowns> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString
    ? JSON.parse(tokenString)
    : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);

  const response = await instance.get(API_ROUTES.USERS_FILTER_DROPDOWNS);
  const root = response?.data?.data ?? response?.data ?? null;
  if (!root || typeof root !== "object") return EMPTY_RESPONSE;

  const r = root as {
    statuses?: unknown;
    kyc_statuses?: unknown;
    ftd?: unknown;
    states?: unknown;
  };
  return {
    statuses: normalizeList(r.statuses),
    kyc_statuses: getKycStatusFilterOptions(),
    ftd: normalizeList(r.ftd),
    states: normalizeList(r.states),
  };
}

export function useUsersFilterDropdowns() {
  return useQuery<UsersFilterDropdowns, Error>({
    queryKey: [GET_USERS_FILTER_DROPDOWNS_QUERY_KEY],
    queryFn: fetchUsersFilterDropdowns,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export default useUsersFilterDropdowns;
